import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { presenceAPI, checkoutAdminAPI, validationAPI, adminGroupsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';
import { isItalianHoliday } from '../utils/holidays';
import { useSearchParams, Navigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

interface DayRecord {
  date: string;
  dayOfWeek: string;
  check_in1: string | null;
  check_out1: string | null;
  check_in2: string | null;
  check_out2: string | null;
  total_hours: number | null;
  status: 'complete' | 'incomplete' | 'holiday' | 'absence';
  notes: string | null;
}

const Timesheet: React.FC = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [isInAdminManagedGroup, setIsInAdminManagedGroup] = useState<boolean | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [canExportTimesheet, setCanExportTimesheet] = useState(false);
  const [loadingExportPolicy, setLoadingExportPolicy] = useState(true);
  
  // Verifica se l'utente appartiene a un gruppo con gestione admin
  useEffect(() => {
    let cancelled = false;
    
    const checkAdminManagedGroup = async () => {
      if (!user) {
        if (!cancelled) {
          setCheckingAccess(false);
          setIsInAdminManagedGroup(false);
        }
        return;
      }
      
      // Se è employee, verifica se appartiene a un gruppo con gestione admin
      if (user.role === 'employee') {
        try {
          const response = await adminGroupsAPI.isEmployeeInAdminManagedGroup();
          if (!cancelled) {
            setIsInAdminManagedGroup(response.is_in_admin_managed_group || false);
            setCheckingAccess(false);
          }
        } catch (err) {
          console.error('[Timesheet] Errore verifica gruppo con gestione admin:', err);
          if (!cancelled) {
            setIsInAdminManagedGroup(false);
            setCheckingAccess(false);
          }
        }
      } else {
        if (!cancelled) {
          setIsInAdminManagedGroup(false);
          setCheckingAccess(false);
        }
      }
    };
    
    checkAdminManagedGroup();
    
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const loadExportPolicy = async () => {
      if (!user) {
        if (!cancelled) {
          setCanExportTimesheet(false);
          setLoadingExportPolicy(false);
        }
        return;
      }

      try {
        if (!cancelled) setLoadingExportPolicy(true);
        const response = await adminGroupsAPI.getTimesheetExportPolicy();
        if (!cancelled) {
          setCanExportTimesheet(response.allowed !== false);
        }
      } catch {
        if (!cancelled) {
          // fallback restrittivo: in caso di errore policy, blocca export
          setCanExportTimesheet(false);
        }
      } finally {
        if (!cancelled) setLoadingExportPolicy(false);
      }
    };

    loadExportPolicy();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);
  
  // Leggi i parametri URL se presenti
  const urlYear = searchParams.get('year');
  const urlMonth = searchParams.get('month');
  const urlUserId = searchParams.get('user_id');
  
  const [year, setYear] = useState(
    urlYear ? parseInt(urlYear) : new Date().getFullYear()
  );
  const [month, setMonth] = useState(
    urlMonth ? parseInt(urlMonth) : new Date().getMonth() + 1
  );
  const [targetUserId, setTargetUserId] = useState<number | null>(
    urlUserId ? parseInt(urlUserId) : null
  );
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<DayRecord[]>([]);
  const [error, setError] = useState('');
  const [totalHours, setTotalHours] = useState(0);
  const [workingDays, setWorkingDays] = useState(0);
  
  // Stati per validazione
  const [validationPreview, setValidationPreview] = useState<any>(null);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationNotes, setValidationNotes] = useState('');

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  const loadTimesheet = async () => {
    try {
      setLoading(true);
      setError('');

      // Calcola il range di date per il mese selezionato
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      // Se c'è un targetUserId (da URL), usa getUserHistory per admin, altrimenti getHistory
      let presences: any[] = [];
      let expectedDaily: Record<string, number> = {};
      const readExpectedDaily = (payload: any) => {
        const raw = payload?.expected_hours_per_day;
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
          return;
        }
        Object.entries(raw).forEach(([key, value]) => {
          const numericValue = typeof value === 'number' ? value : parseFloat(String(value));
          if (!Number.isNaN(numericValue)) {
            expectedDaily[key] = numericValue;
          }
        });
      };
      if (targetUserId && user?.role === 'admin') {
        // Admin che visualizza cartellino di altro utente
        const adminData = await checkoutAdminAPI.getUserHistory(targetUserId, startDate, endDate);
        presences = Array.isArray(adminData.history) ? adminData.history : [];
        readExpectedDaily(adminData);
      } else {
        // Utente normale o admin che visualizza il proprio cartellino
        const userData = await presenceAPI.getHistory(startDate, endDate);
        presences = Array.isArray(userData) ? userData : (Array.isArray(userData.history) ? userData.history : []);
        readExpectedDaily(userData);
      }
      
      // Genera tutti i giorni del mese
      const daysInMonth = new Date(year, month, 0).getDate();
      const days: DayRecord[] = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const dayOfWeek = dayNames[date.getDay()];
        const isWeekend = isItalianHoliday(date);
        
        // Cerca se c'è una presenza per questo giorno
        const presence = presences.find((p: any) => {
          const presDate = new Date(p.check_in);
          return presDate.getDate() === day && 
                 presDate.getMonth() === month - 1 && 
                 presDate.getFullYear() === year;
        });

        let status: DayRecord['status'] = isWeekend ? 'holiday' : 'absence';
        let check_in1: string | null = null;
        let check_out1: string | null = null;
        let check_in2: string | null = null;
        let check_out2: string | null = null;
        let total_hours: number | null = null;

        if (presence) {
          check_in1 = new Date(presence.check_in).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
          check_out1 = presence.check_out 
            ? new Date(presence.check_out).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
            : null;
          total_hours = presence.work_hours || 0;
          
          // Se c'è checkout, il giorno è completo
          if (presence.check_out) {
            status = isWeekend ? 'holiday' : 'complete';
          } else if (presence.check_in) {
            status = 'incomplete';
          }
        }

        const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const expectedDayHours = expectedDaily[dateKey];
        if (expectedDayHours != null && expectedDayHours <= 0 && status === 'absence') {
          status = 'holiday';
        }

        days.push({
          date: dateKey,
          dayOfWeek,
          check_in1,
          check_out1,
          check_in2,
          check_out2,
          total_hours,
          status,
          notes: presence?.notes || null,
        });
      }

      setRecords(days);
      
      // Calcola totali
      const workedHours = days.reduce((sum, day) => sum + (day.total_hours || 0), 0);
      const workedDays = days.filter(d => d.status === 'complete').length;
      setTotalHours(workedHours);
      setWorkingDays(workedDays);

    } catch (err: any) {
      setError('Errore nel caricamento del cartellino mensile');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    if (!canExportTimesheet) {
      setError('Esportazione cartellino non consentita dalla policy attiva');
      return;
    }

    if (records.length === 0) {
      alert('Carica il cartellino prima di esportare');
      return;
    }

    // Prepara i dati per l'export
    const exportData = records.map((record) => {
      const date = new Date(record.date);
      const dayOfMonth = date.getDate();
      
      // Determina lo stato
      let statusText = '';
      switch (record.status) {
        case 'complete':
          statusText = 'Completo';
          break;
        case 'incomplete':
          statusText = 'Incompleto';
          break;
        case 'holiday':
          statusText = 'Festivo';
          break;
        case 'absence':
          statusText = 'Assenza';
          break;
        default:
          statusText = '-';
      }

      return {
        'Giorno': dayOfMonth,
        'Giorno Settimana': record.dayOfWeek,
        'Data': record.date,
        'Entrata 1': record.check_in1 || '-',
        'Uscita 1': record.check_out1 || '-',
        'Entrata 2': record.check_in2 || '-',
        'Uscita 2': record.check_out2 || '-',
        'Ore Totali': record.total_hours ? record.total_hours.toFixed(2) : '-',
        'Stato': statusText,
        'Note': record.notes || '-',
      };
    });

    // Aggiungi riga di riepilogo
    const summaryRow = {
      'Giorno': 'RIEPILOGO',
      'Giorno Settimana': '',
      'Data': '',
      'Entrata 1': '',
      'Uscita 1': '',
      'Entrata 2': '',
      'Uscita 2': '',
      'Ore Totali': totalHours.toFixed(2),
      'Stato': '',
      'Note': '',
    };

    // Crea il workbook
    const ws = XLSX.utils.json_to_sheet([...exportData, summaryRow]);
    const wb = XLSX.utils.book_new();
    
    // Imposta larghezza colonne
    const colWidths = [
      { wch: 8 },  // Giorno
      { wch: 12 }, // Giorno Settimana
      { wch: 12 }, // Data
      { wch: 10 }, // Entrata 1
      { wch: 10 }, // Uscita 1
      { wch: 10 }, // Entrata 2
      { wch: 10 }, // Uscita 2
      { wch: 12 }, // Ore Totali
      { wch: 15 }, // Stato
      { wch: 30 }, // Note
    ];
    ws['!cols'] = colWidths;

    // Aggiungi informazioni utente come header
    const userInfo = [
      ['DIPENDENTE', user ? `${user.nome} ${user.cognome}` : '-'],
      ['Mese', `${monthNames[month - 1]} ${year}`],
      [''],
      ['RIEPILOGO MENSILE'],
      ['Totale Ore', totalHours.toFixed(2)],
      ['Giorni Lavorativi', workingDays],
      [''],
    ];

    // Crea un secondo foglio con le informazioni
    const infoWs = XLSX.utils.aoa_to_sheet(userInfo);
    XLSX.utils.book_append_sheet(wb, infoWs, 'Info');
    XLSX.utils.book_append_sheet(wb, ws, 'Cartellino');

    // Genera il nome file
    const userName = user ? `${user.nome}_${user.cognome}`.replace(/\s+/g, '_') : 'Dipendente';
    const filename = `Cartellino_${userName}_${monthNames[month - 1]}_${year}.xlsx`;

    // Scarica il file
    XLSX.writeFile(wb, filename);
  };

  const loadValidationPreview = async (userId: number, selectedYear: number, selectedMonth: number) => {
    if (!token) return;
    
    setLoadingValidation(true);
    try {
      // Usa validationAPI invece di costruire manualmente l'URL
      const userIdToUse = userId && userId !== user?.id ? userId : undefined;
      const data = await validationAPI.getValidationPreview(selectedYear, selectedMonth, userIdToUse);
      setValidationPreview(data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Dipendente non trovato. Contatta l\'amministratore per associare un profilo dipendente al tuo account.');
      } else {
        setError(err.response?.data?.error || err.message || 'Errore nel caricamento della preview di validazione');
      }
      setValidationPreview(null);
    } finally {
      setLoadingValidation(false);
    }
  };

  // Carica dati al cambio mese/anno
  useEffect(() => {
    loadTimesheet();
    if (user?.id) {
      loadValidationPreview(user.id, year, month);
    }
  }, [year, month, user?.id]);

  // Se l'utente ha ruolo "employee", reindirizza alla dashboard dipendente
  // Oppure se appartiene a un gruppo con gestione admin, reindirizza alla dashboard
  if (checkingAccess) {
    return null;
  }

  if (user?.role === 'employee' || isInAdminManagedGroup === true) {
    return <Navigate to="/employee-dashboard" replace />;
  }

  const handleOpenValidationDialog = async () => {
    if (!user?.id) return;
    
    // Carica la preview se non è già caricata
    if (!validationPreview) {
      await loadValidationPreview(user.id, year, month);
    }
    
    setValidationDialogOpen(true);
  };

  const handleValidatePresence = async () => {
    if (!user?.id || !token) return;
    
    setLoadingValidation(true);
    setError('');
    try {
      await validationAPI.validatePresence({
        year,
        month,
        validation_notes: validationNotes || undefined,
      });
      
      setValidationDialogOpen(false);
      setValidationNotes('');
      await loadValidationPreview(user.id, year, month);
      setError('');
      // Mostra messaggio di successo
      alert('Cartellino validato con successo!');
    } catch (err: any) {
      const errorData = err.response?.data;
      let errorMessage = errorData?.error || err.message || 'Errore durante la validazione';
      
      // Se c'è un required_level, aggiungilo al messaggio
      if (errorData?.required_level) {
        const levelNames: { [key: string]: string } = {
          'employee': 'dipendente',
          'group_admin': 'admin di gruppo',
          'super_admin': 'super admin'
        };
        const levelName = levelNames[errorData.required_level] || errorData.required_level;
        errorMessage += ` (richiesto livello: ${levelName})`;
      }
      
      setError(errorMessage);
    } finally {
      setLoadingValidation(false);
    }
  };

  const getStatusIcon = (status: DayRecord['status']) => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'incomplete':
        return <ErrorIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'holiday':
        return <CalendarIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
      default:
        return <span>-</span>;
    }
  };

  const getStatusColor = (status: DayRecord['status']) => {
    switch (status) {
      case 'complete':
        return 'success';
      case 'incomplete':
        return 'warning';
      case 'holiday':
        return 'default';
      default:
        return 'error';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
              Cartellino Mensile
            </Typography>
            {user && (
              <Typography variant="body1" color="text.secondary">
                {user.nome} {user.cognome} - {monthNames[month - 1]} {year}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Valida Cartellino">
              <span>
                <IconButton
                  onClick={handleOpenValidationDialog}
                  disabled={loadingValidation}
                  color="primary"
                  sx={{
                    opacity: loadingValidation ? 0.5 : 1,
                  }}
                >
                  <CheckCircleIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Aggiorna">
              <IconButton onClick={loadTimesheet} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Stampa">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            {(canExportTimesheet && !loadingExportPolicy) && (
              <Tooltip title="Scarica Excel">
                <IconButton onClick={handleDownload}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Filtri */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Mese</InputLabel>
            <Select
              value={month}
              label="Mese"
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {monthNames.map((name, index) => (
                <MenuItem key={index} value={index + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={year}
              label="Anno"
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={loadTimesheet}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ScheduleIcon />}
            sx={{ backgroundColor: '#0d47a1', '&:hover': { backgroundColor: '#1565c0' } }}
          >
            Carica Cartellino
          </Button>
        </Box>

        {/* Statistiche */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="body2" color="text.secondary">
                Ore Totali
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                {totalHours.toFixed(2)}h
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: '#fff3e0' }}>
              <Typography variant="body2" color="text.secondary">
                Giorni Lavorati
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                {workingDays}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: '#f3e5f5' }}>
              <Typography variant="body2" color="text.secondary">
                Media Ore/Giorno
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                {workingDays > 0 ? (totalHours / workingDays).toFixed(2) : '0.00'}h
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabella Cartellino */}
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Giorno</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Entrata 1</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Uscita 1</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Entrata 2</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Uscita 2</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Totale Ore</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nessun dato disponibile per questo periodo</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow
                    key={record.date}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      backgroundColor: record.status === 'holiday' ? '#fafafa' : 'white',
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {record.date.split('-')[2]} {record.dayOfWeek}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {record.check_in1 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {record.check_out1 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {record.check_in2 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {record.check_out2 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {record.total_hours ? `${record.total_hours.toFixed(2)}h` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {getStatusIcon(record.status)}
                        <Chip
                          label={
                            record.status === 'complete' ? 'Completo' :
                            record.status === 'incomplete' ? 'Incompleto' :
                            record.status === 'holiday' ? 'Festivo' :
                            'Assenza'
                          }
                          color={getStatusColor(record.status) as any}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog di conferma validazione */}
        <Dialog
          open={validationDialogOpen}
          onClose={() => setValidationDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Conferma Validazione Cartellino
          </DialogTitle>
          <DialogContent>
            {!validationPreview ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <DialogContentText>
                  Stai per validare il cartellino mensile. Una volta validato, per modificarlo sarà necessario revocare la validazione.
                </DialogContentText>
                {validationPreview && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>Riepilogo:</Typography>
                    <Typography variant="body2">Ore presenza: {validationPreview.total_presence_hours?.toFixed(2) || '0.00'}h</Typography>
                    <Typography variant="body2">Ore ferie: {validationPreview.total_leave_hours?.toFixed(2) || '0.00'}h</Typography>
                    <Typography variant="body2">Ore permesso: {validationPreview.total_permission_hours?.toFixed(2) || '0.00'}h</Typography>
                    <Typography variant="body2">Altre assenze: {validationPreview.total_other_absence_hours?.toFixed(2) || '0.00'}h</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                      Totale ore effettive: {((validationPreview.total_presence_hours || 0) + (validationPreview.total_permission_hours || 0) + (validationPreview.total_leave_hours || 0) + (validationPreview.total_other_absence_hours || 0)).toFixed(2)}h
                    </Typography>
                    {validationPreview.total_expected_hours != null && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                          Ore attese totali: {validationPreview.total_expected_hours.toFixed(2)}h
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'bold', 
                          mt: 1,
                          color: validationPreview.total_expected_hours - ((validationPreview.total_presence_hours || 0) + (validationPreview.total_permission_hours || 0) + (validationPreview.total_leave_hours || 0) + (validationPreview.total_other_absence_hours || 0)) > 0.01 ? 'error.main' : 'success.main'
                        }}>
                          Differenza: {(validationPreview.total_expected_hours - ((validationPreview.total_presence_hours || 0) + (validationPreview.total_permission_hours || 0) + (validationPreview.total_leave_hours || 0) + (validationPreview.total_other_absence_hours || 0))).toFixed(2)}h
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
                <TextField
                  autoFocus
                  margin="dense"
                  label="Note (opzionale)"
                  fullWidth
                  multiline
                  rows={3}
                  value={validationNotes}
                  onChange={(e) => setValidationNotes(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setValidationDialogOpen(false);
              setValidationNotes('');
            }}>
              Annulla
            </Button>
            <Button
              onClick={handleValidatePresence}
              variant="contained"
              color="primary"
              disabled={loadingValidation || !validationPreview}
            >
              {loadingValidation ? 'Validazione...' : 'Conferma Validazione'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default Timesheet;
